import { FaqComponent } from '@shared/components/faq';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { TranslateModule } from '@ngx-translate/core';
import { MatTableModule } from '@angular/material/table';
import { HomeCardComponent } from '@shared/components/home-card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReportingToolTemplateSelectionComponent } from '@shared/components/reporting-tool-template-selection';
import { UserSettingsComponent } from '@shared/components/user-settings';
import { ReportingToolUserPickerComponent } from '@shared/components/reporting-tool-user-picker';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CustomMatPaginatorIntl } from '@shared/components/intls/custom-mat-paginator.int';
import { ReportingToolChipComponent } from '@shared/components/reporting-tool-chip';
import { MatDialogModule } from '@angular/material/dialog';
import { LogoComponent } from '@shared/components/logo';
import { FileSizePipe } from '@shared/pipes/file-size.pipe';
import { TemplatedDialogComponent } from '@shared/components/templated-dialog';
import { ReportingToolResponsibleSelectionComponent } from '@shared/components/reporting-tool-responsible-selection';
import { NgModule } from '@angular/core';
import { ReportTileComponent } from '@shared/components/report-tile';
import { WelcomeBannerComponent } from '@shared/components/welcome-banner';
import { LoadingIndicatorComponent } from '@shared/components/loading-indicator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { HomeWidgetComponent } from '@shared/components/home-widget';
import { MatTabsModule } from '@angular/material/tabs';
import { UserPickerComponent } from '@shared/components/user-picker';
import { HeaderComponent } from '@shared/components/header';
import { SummaryReportTileComponent } from '@shared/components/summary-report-tile';
import { SearchBarComponent, SearchBarFilterComponent } from '@shared/components/search-bar';
import { ScdExternDialogComponent } from '@shared/components/scd-extern-dialog';
import { LanguageToggleComponent } from '@shared/components/language-toggle';
import { ReportingToolUserSelectionComponent } from '@shared/components/reporting-tool-user-selection';
import { FooterComponent } from '@shared/components/footer';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import { ReportingToolResponsiblePickerComponent } from '@shared/components/reporting-tool-responsible-picker';
import { MatNativeDateModule } from '@angular/material/core';
import { ReportingToolTemplatePickerComponent } from '@shared/components/reporting-tool-template-picker';
import { PaginatorComponent } from '@shared/components/paginator';
import { DragDropDirective } from '@shared/directives/drag-drop.directive';
import { FocusDirective } from '@shared/directives/input-focus.directive';
import { SortAlphabeticallyPipe } from '@shared/pipes';
import { MatRadioModule } from '@angular/material/radio';
import { BugReportComponent } from '@shared/components/bug-report/bug-report.component';
import { BugReportButtonComponent } from '@shared/components/bug-report/bug-report-button.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { UserItemComponent } from '@shared/components/user-item/user-item.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SnacksComponent } from '@shared/components/snacks/snacks.component';
import { SnacksItemComponent } from '@shared/components/snacks/snacks-item/snacks-item.component';
import { MatBadgeModule } from '@angular/material/badge';
import { TemplateItemComponent } from '@shared/components/template-item/template-item.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { DropZoneComponent } from '@shared/components/drop-zone/drop-zone.component';
import { ReportingToolUserMenuComponent } from '@shared/components/user-menu/reporting-tool-user-menu.component';
import { NgxEditorModule } from 'ngx-editor';
import {
    AvatarBaseDirective,
    AvatarDirective,
    AvatarNameDirective,
    AvatarOrgDirective,
    ButtonComponent,
    CheckboxComponent,
    ChipComponent,
    DateRangePickerComponent,
    FileDropComponent,
    InputComponent,
    OptionComponent,
    RadioComponent,
    RadioGroupComponent,
    SelectComponent,
    SwitchComponent,
    TabComponent,
    TabViewComponent,
    TextareaComponent,
    TooltipDirective
} from '@siemens/component-lib';
import { LanguageSelectionComponent } from './language-selection/language-selection.component';
import { UserCreationComponent } from './user-creation/user-creation.component';
import { CommunicationItemComponent } from './communication/communication-item/communication-item.component';
import { CommunicationComponent } from './communication/communication.component';
import { OrganisationItemComponent } from './organisation-item/organisation-item.component';
import { ExpiringTokenComponent } from './expiring-token/expiring-token.component';
import { EditorComponent } from './editor/editor.component';

const components = [
    PaginatorComponent,
    HeaderComponent,
    LogoComponent,
    SearchBarComponent,
    ScdExternDialogComponent,
    UserPickerComponent,
    SearchBarFilterComponent,
    LoadingIndicatorComponent,
    ReportingToolChipComponent,
    ReportingToolUserPickerComponent,
    ReportingToolUserSelectionComponent,
    ReportingToolTemplateSelectionComponent,
    ReportingToolTemplatePickerComponent,
    ReportingToolResponsibleSelectionComponent,
    ReportingToolResponsiblePickerComponent,
    DropZoneComponent,
    ReportingToolUserMenuComponent,
    FileSizePipe,
    UserSettingsComponent,
    ReportTileComponent,
    SummaryReportTileComponent,
    HomeCardComponent,
    LocalizedDatePipe,
    FooterComponent,
    LanguageToggleComponent,
    FaqComponent,
    HomeWidgetComponent,
    WelcomeBannerComponent,
    TemplatedDialogComponent,
    DragDropDirective,
    FocusDirective,
    SortAlphabeticallyPipe,
    BugReportComponent,
    BugReportButtonComponent,
    UserItemComponent,
    SnacksComponent,
    SnacksItemComponent,
    TemplateItemComponent,
    ExpiringTokenComponent,
    OrganisationItemComponent,
    CommunicationComponent,
    CommunicationItemComponent,
    UserCreationComponent,
    LanguageSelectionComponent,
    EditorComponent
];
const compLibComponents = [
    InputComponent,
    RadioComponent,
    ChipComponent,
    TabViewComponent,
    TabComponent,
    SelectComponent,
    TextareaComponent,
    OptionComponent,
    ButtonComponent,
    DateRangePickerComponent,
    FileDropComponent,
    RadioGroupComponent,
    CheckboxComponent,
    TooltipDirective,
    AvatarDirective,
    AvatarNameDirective,
    AvatarOrgDirective,
    AvatarBaseDirective,
    SwitchComponent
];

const modules = [
    CommonModule,
    RouterModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatDialogModule,
    FontAwesomeModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    DragDropModule,
    MatSnackBarModule,
    MatSelectModule,
    MatChipsModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslateModule,
    MatTabsModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatButtonToggleModule,
    MatBadgeModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatLuxonDateModule,
    NgxEditorModule
];

@NgModule({
    declarations: [...components],
    imports: [
        ...modules,
        ...compLibComponents,
        NgCircleProgressModule.forRoot({
            radius: 100,
            outerStrokeWidth: 16,
            innerStrokeWidth: 8,
            outerStrokeColor: '#78C000',
            innerStrokeColor: '#C7E596',
            animationDuration: 300
        })
    ],
    exports: [...modules, ...components, NgCircleProgressModule, ...compLibComponents],
    providers: [
        { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
        { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
    ]
})
export class ComponentsModule {}
